import { type FC, useEffect, useMemo, useRef } from 'react';

import { Loading } from '@/components/ui/old/Loading';
import type { Alert } from '@/entities/alert';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import type { AlertsFilters as Filters } from '@/types/alerts-filters';
import { cls } from '@/utils/cls';

import { AlertsSidebarEmptyState } from './alerts-sidebar-empty-state';
import { AlertsSidebarItem } from './alerts-sidebar-item';

export type AlertsSidebarProps = {
  selectedId?: string;
  selectedType?: string;
  selectedLevel?: string;
  filters: Filters;
  onSelect: (alert: Alert) => void;
  className?: string;
  alerts?: Alert[];
  isLoading?: boolean;
};
export const AlertsSidebar: FC<AlertsSidebarProps> = ({
  selectedId,
  selectedType,
  selectedLevel,
  filters,
  isLoading,
  alerts = [],
  onSelect,
  className,
}) => {
  const selectedRef = useRef<HTMLDivElement>(null);
  const {
    asset,
    isLoading: isAssetLoading,
    assetId,
    isMobility,
    vehicle,
  } = useCurrentAsset();

  const isMissing = useMemo(
    function () {
      if (!assetId || isAssetLoading) {
        return false;
      }

      if (isMobility) {
        return !vehicle;
      }

      return !asset?.fullFrontendId;
    },
    [asset?.fullFrontendId, assetId, isAssetLoading, isMobility, vehicle],
  );

  useEffect(() => {
    // Scroll to the selected alert when data is loaded
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [alerts]);

  if (isLoading || isAssetLoading) {
    return <Loading />;
  }

  return (
    <div
      data-testid="alerts-sidebar"
      className={cls(
        'bg-gray relative h-full min-w-60 max-w-96 flex-shrink-0 flex-grow overflow-hidden',
        className,
      )}
    >
      <div
        role="list"
        className="relative h-full overflow-y-auto overflow-x-hidden bg-white"
      >
        {!isMissing && alerts.length > 0 ? (
          alerts.map(function (alert) {
            const isSelected =
              alert.fullFrontendId === selectedId &&
              alert.type === selectedType &&
              alert.level === selectedLevel;

            return (
              <AlertsSidebarItem
                key={`${alert.fullFrontendId}-${alert.type}`}
                alert={alert}
                selected={isSelected}
                onClick={() => onSelect(alert)}
                ref={isSelected ? selectedRef : null}
              />
            );
          })
        ) : (
          <AlertsSidebarEmptyState isMissing={isMissing} filters={filters} />
        )}
      </div>
    </div>
  );
};
